import { IconButton } from "@smartsuite/react-ui/lib/components/buttons/IconButton/IconButton";
import { Tooltip } from "@smartsuite/react-ui/lib/components/help/Tooltip/Tooltip";
import type { Placements } from "@smartsuite/react-ui/lib/components/help/Tooltip/Tooltip.constants";
import { Title } from "@smartsuite/react-ui/lib/components/typography/Title/Title";
import { Colors, GrayColors } from "@smartsuite/types";
import type React from "react";
import { type FC, memo, useCallback, useRef } from "react";
import { PageDesignerDotsMenu } from "../PageDesignerDotsMenu/PageDesignerDotsMenu";
import { showElementWithoutImplementation } from "../utils";

const iconButtonStyles = {
  color: GrayColors["G-3"],
  hoverColor: Colors.dGrey,
  backgroundColor: GrayColors["G-1"],
};

const hamburgerTooltipPlacements: Placements = ["top"];

export interface PageDesignerHeaderProps {
  name: string;
  onDelete: () => Promise<void>;
  onRename: (name: string) => void;
  onClose: () => void;
  onDuplicate: () => void;
}

const PageDesignerHeaderRaw: FC<PageDesignerHeaderProps> = ({
  name,
  onDelete,
  onRename,
  onClose,
  onDuplicate,
}) => {
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleRenameClick = useCallback(() => {
    if (nameInputRef?.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Enter" && nameInputRef?.current) {
      nameInputRef.current.blur();
    }
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onRename(event.target.value),
    [onRename],
  );

  const handleBlur = useCallback(() => onRename(name), [name, onRename]);

  return (
    <div className="page-designer-header">
      <span className="page-designer-header__hidden-content">
        hidden-content
      </span>
      <div className="page-designer-header__info">
        {showElementWithoutImplementation && (
          <Tooltip
            display="inline-flex"
            padding={2}
            placements={hamburgerTooltipPlacements}
            text="Manage Templates"
            target={
              <IconButton
                size="M"
                name="list-mode"
                ariaLabel="Hamburger menu"
                className="page-designer-header__hamburger"
              />
            }
          />
        )}

        <Title weight="semibold" size="xl">
          Document Designer
        </Title>

        <Title
          weight="regular"
          size="xl"
          color="secondary"
          className="page-designer-header__separator"
        >
          /
        </Title>

        {/* biome-ignore lint/a11y/noPositiveTabindex lint/a11y/noNoninteractiveTabindex: the title is a focus target for its inline editor, and its tab order is a UX decision, not a lint fix */}
        <div className="page-designer-header-title" tabIndex={1}>
          <div className="page-designer-header-title__text">{name}</div>
          <div className="page-designer-header-title__input">
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
        </div>

        <div className="page-designer-header__buttons">
          {showElementWithoutImplementation && (
            <IconButton size="S" name="info-filled" ariaLabel="Info Button" />
          )}

          <PageDesignerDotsMenu
            onDelete={onDelete}
            onRename={handleRenameClick}
            onDuplicate={onDuplicate}
          />
        </div>
      </div>

      <div className="page-designer-header__actions">
        <IconButton
          size="M"
          name="close"
          kind="round"
          ariaLabel="Close Button"
          className="page-designer-header__action"
          {...iconButtonStyles}
          onClick={onClose}
        />
      </div>
    </div>
  );
};
export const PageDesignerHeader = memo(PageDesignerHeaderRaw);
